import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// Imperial Monitor — single-strategy dashboard view of imperial_decisions.
// Polls /strategies/api/strategies/imperial/decisions/ every 20s. Imperial
// is NQ 30m ORB Swing Long (v3.1) — long-only, single-position cross-day.
//
// Event vocabulary (kind × action):
//   ENTRY GO            -> TRADE (PHASE_2/3) or OBSERVE (PHASE_0/1)
//   ENTRY REJECT_DUPLICATE / BLOCK -> BLOCK
//   EXTEND_EXIT EXTEND  -> OBSERVE (sub-event, host re-broadcast)
//   EXTEND_EXIT NO_OPEN_TRADE / NOT_LATER -> BLOCK
//   TIME_EXIT / CHECKPOINT_EXIT -> EXIT (host closed) or BLOCK (orphan)
//   WED_SKIP            -> BLOCK
//
// Phase 0/1 are log-only (the engine emits OBSERVE rows). Phase 2/3 are
// live. The kit enforces single-position at the engine layer — no need
// for the cross-book exit_manager gate.

const REFRESH_MS = 20000;

const ACTION_COLORS = {
  TRADE:       T.bull,
  OBSERVE:     '#3b82f6',
  EXIT:        '#a855f7',
  'STOP LOSS': '#ef4444',
  BLOCK:       T.bear,
};

const KIND_COLORS = {
  ENTRY:           '#10b981',
  EXTEND_EXIT:     '#f59e0b',
  TIME_EXIT:       '#a855f7',
  CHECKPOINT_EXIT: '#a855f7',
  STOP:            '#ef4444',
  WED_SKIP:        T.textMuted,
};

const PHASE_COLORS = {
  PHASE_0: T.textMuted,
  PHASE_1: '#3b82f6',
  PHASE_2: '#10b981',
  PHASE_3: '#10b981',
};

const todayETStr = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}`;
  } catch { return iso; }
}

function Pill({ value, color }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
      textTransform: 'uppercase',
      background: `${color || T.border}22`,
      border: `1px solid ${color || T.border}66`,
      color: color || T.text,
    }}>{String(value)}</span>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 140px', padding: '12px 14px',
                  borderRadius: 6, background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, color: color || T.text,
                    fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

export default function ImperialMonitor() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [kindFilter,   setKindFilter]   = useState('');
  const [closedFilter, setClosedFilter] = useState('');     // '' | 'true' | 'false'
  const [limit, setLimit]   = useState(100);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/imperial/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (kindFilter)   url += `&kind=${kindFilter}`;
      if (closedFilter) url += `&closed=${closedFilter}`;
      // Imperial backend accepts `since=<iso>` for a lower bound. We feed it
      // From (start of dateFrom day, ET) — and post-filter on dateTo here.
      if (dateFrom) {
        url += `&since=${dateFrom}T00:00:00-04:00`;
      }
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      let next = j.decisions || [];

      // Post-filter on dateTo (inclusive ET-day) since the backend doesn't
      // currently accept an upper bound. If dateTo is unset, this is a no-op.
      if (dateTo) {
        const cutoff = new Date(`${dateTo}T23:59:59-04:00`).getTime();
        next = next.filter(r => {
          const t = new Date(r.signal_ts || r.received_at).getTime();
          return Number.isFinite(t) && t <= cutoff;
        });
      }

      // Imperial Monitor noise filters (applied client-side):
      //
      // 1. Hide PHASE_0 / PHASE_1 audit rows. Imperial moved to PHASE_2 on
      //    2026-05-12 18:45 UTC; rows before that carry an obsolete phase tag
      //    that no longer reflects the live config.
      //
      // 2. Hide orphan EXTEND_EXIT / TIME_EXIT / CHECKPOINT_EXIT rows whose
      //    reason is "No open ENTRY to extend/close". Pine's exit-event
      //    emitters fire on every relevant 30m bar without knowing whether
      //    Imperial actually holds an open position — the backend correctly
      //    BLOCKs them but they were cluttering the audit.
      //
      // 3. Hide filter-blocked ENTRY rows (kind=ENTRY AND action=BLOCK).
      //    These are misleading because the badge says "ENTRY" even though
      //    no trade actually fired. WED_SKIP rows (kind=WED_SKIP) and real
      //    ENTRY GO rows still show.
      next = next.filter(r => {
        const phase  = (r.sizing_phase || r.phase || '').toUpperCase();
        const kind   = (r.kind         || '').toUpperCase();
        const action = (r.action_label || r.action || '').toUpperCase();
        const reason = (r.reason       || '').toLowerCase();

        if (phase === 'PHASE_0' || phase === 'PHASE_1') return false;

        const isOrphanExit =
          (kind === 'EXTEND_EXIT' || kind === 'TIME_EXIT' || kind === 'CHECKPOINT_EXIT')
          && reason.startsWith('no open entry');
        if (isOrphanExit) return false;

        if (kind === 'ENTRY' && action === 'BLOCK') return false;

        return true;
      });

      setRows(next);
      setLastFetched(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally { setLoading(false); }
  }, [actionFilter, kindFilter, closedFilter, limit, dateFrom, dateTo]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchRows]);

  // Aggregate stat cards
  const counts = rows.reduce((acc, r) => {
    acc.total += 1;
    if      (r.action === 'TRADE')   acc.trades   += 1;
    else if (r.action === 'OBSERVE') acc.observes += 1;
    else if (r.action === 'EXIT' || r.action === 'STOP LOSS') acc.exits += 1;  // resolved exits
    else if (r.action === 'BLOCK')   acc.blocks   += 1;
    if (typeof r.pl_usd === 'number') acc.netPl += r.pl_usd;  // pl_usd only on exit rows
    return acc;
  }, { total: 0, trades: 0, observes: 0, exits: 0, blocks: 0, netPl: 0 });

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.bg }}>
    <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)', maxWidth: 1700, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                    paddingBottom: 14, borderBottom: `1px solid ${T.border}`,
                    marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
          Imperial Monitor
        </h1>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          v3.1 · NQ 30m ORB Swing Long · single-position cross-day
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8,
                      alignItems: 'center', flexWrap: 'wrap',
                      justifyContent: 'flex-end', flex: '1 1 360px' }}>
          <label style={{ fontSize: 11, color: T.textMuted }}>From</label>
          <input type="date" value={dateFrom} max={todayETStr()}
                 onChange={(e) => setDateFrom(e.target.value)}
                 style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4,
                          border: `1px solid ${T.border}`, background: T.bg, color: T.text,
                          fontFamily: 'Roboto Mono, monospace',
                          opacity: dateFrom ? 1 : 0.7 }} />
          <label style={{ fontSize: 11, color: T.textMuted }}>To</label>
          <input type="date" value={dateTo} max={todayETStr()}
                 onChange={(e) => setDateTo(e.target.value)}
                 title="Blank = same day as From (treated as upper bound only)."
                 style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4,
                          border: `1px solid ${T.border}`, background: T.bg, color: T.text,
                          fontFamily: 'Roboto Mono, monospace',
                          opacity: dateTo ? 1 : 0.7 }} />
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                  style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700,
                           letterSpacing: '0.05em', textTransform: 'uppercase',
                           borderRadius: 4, cursor: 'pointer',
                           border: `1px solid ${T.border}`,
                           background: 'transparent', color: T.textMuted }}>Clear</button>
          {[
            { lbl: '30D',  back: () => { const d = new Date(); d.setDate(d.getDate()-30); return d; } },
            { lbl: '3MO',  back: () => { const d = new Date(); d.setMonth(d.getMonth()-3); return d; } },
            { lbl: '10MO', back: () => { const d = new Date(); d.setMonth(d.getMonth()-10); return d; } },
          ].map(p => (
            <button key={p.lbl}
                    onClick={() => {
                      setDateFrom(p.back().toLocaleDateString(
                        'en-CA', { timeZone: 'America/New_York' }));
                      setDateTo(todayETStr());
                    }}
                    title={`Last ${p.lbl.toLowerCase()}`}
                    style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700,
                             letterSpacing: '0.05em', textTransform: 'uppercase',
                             borderRadius: 4, cursor: 'pointer',
                             border: `1px solid ${T.border}`,
                             background: 'transparent', color: T.textMuted }}>{p.lbl}</button>
          ))}
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                           border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All actions</option>
            <option value="GO">GO only</option>
            <option value="EXTEND">EXTEND only</option>
            <option value="BLOCK">BLOCK only</option>
            <option value="REJECT_DUPLICATE">DUPLICATE only</option>
          </select>
          <select value={kindFilter} onChange={e => setKindFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                           border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All kinds</option>
            <option value="ENTRY">ENTRY</option>
            <option value="EXTEND_EXIT">EXTEND_EXIT</option>
            <option value="TIME_EXIT">TIME_EXIT</option>
            <option value="CHECKPOINT_EXIT">CHECKPOINT_EXIT</option>
            <option value="WED_SKIP">WED_SKIP</option>
          </select>
          <select value={closedFilter} onChange={e => setClosedFilter(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                           border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value="">All</option>
            <option value="true">Closed</option>
            <option value="false">Open</option>
          </select>
          <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))}
                  style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4,
                           border: `1px solid ${T.border}`, background: T.bg, color: T.text }}>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
            <option value={200}>200 rows</option>
          </select>
          <button onClick={fetchRows} disabled={loading}
                  style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700,
                           letterSpacing: '0.07em', textTransform: 'uppercase',
                           borderRadius: 4, cursor: loading ? 'wait' : 'pointer',
                           border: 'none',
                           background: 'rgba(124,58,237,0.18)', color: '#a855f7' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 12, borderRadius: 4,
                      background: 'rgba(239,68,68,0.1)',
                      border: `1px solid ${T.bear}`, color: T.bear, fontSize: 12 }}>
          Error: {error}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <Stat label="Total events" value={counts.total}     color={T.text} />
        <Stat label="TRADE"        value={counts.trades}    color={ACTION_COLORS.TRADE} />
        <Stat label="OBSERVE"      value={counts.observes}  color={ACTION_COLORS.OBSERVE} />
        <Stat label="EXIT"         value={counts.exits}     color={ACTION_COLORS.EXIT} />
        <Stat label="BLOCK"        value={counts.blocks}    color={ACTION_COLORS.BLOCK} />
        <Stat label="Net P/L ($)"
              value={`${counts.netPl >= 0 ? '+' : ''}${counts.netPl.toFixed(0)}`}
              color={counts.netPl >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8,
                    fontFamily: 'Roboto Mono, monospace' }}>
        Decision events · {rows.length} rows · newest first
        {lastFetched && ` · updated ${lastFetched.toLocaleTimeString('en-GB', { hour12: false })} ET`}
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`,
                    borderRadius: 6, background: 'rgba(255,255,255,0.01)' }}>
        <table style={{ width: '100%', minWidth: 1240, borderCollapse: 'collapse',
                        fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)', textAlign: 'left',
                         color: T.textMuted, fontSize: 10, letterSpacing: '0.05em',
                         textTransform: 'uppercase' }}>
              <th style={{ padding: '8px 10px' }}>Time (ET)</th>
              <th style={{ padding: '8px 10px' }}>Kind</th>
              <th style={{ padding: '8px 10px' }}>Action</th>
              <th style={{ padding: '8px 10px' }}>Phase</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Sig $</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>PdH dist</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Bar rng</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Tue ret</th>
              <th style={{ padding: '8px 10px' }}>Bear/Wed</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ctr</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ext#</th>
              <th style={{ padding: '8px 10px' }}>Exit evt</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>P/L pts</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>P/L $</th>
              <th style={{ padding: '8px 10px' }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={15} style={{ padding: '20px 14px', color: T.textMuted,
                                          textAlign: 'center', fontStyle: 'italic' }}>
                  No Imperial decisions in this view. Auto-refreshing every {REFRESH_MS/1000}s.
                </td>
              </tr>
            )}
            {rows.map(r => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ padding: '6px 10px', color: T.text }}>
                  {fmtET(r.signal_ts || r.received_at)}
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <Pill value={r.kind} color={KIND_COLORS[r.kind]} />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <Pill value={r.action} color={ACTION_COLORS[r.action]} />
                </td>
                <td style={{ padding: '6px 10px' }}>
                  <Pill value={r.sizing_phase} color={PHASE_COLORS[r.sizing_phase]} />
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.text }}>
                  {r.sig_price != null ? Number(r.sig_price).toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textDim }}>
                  {r.pdh_dist != null ? Number(r.pdh_dist).toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textDim }}>
                  {r.bar_range != null ? Number(r.bar_range).toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: T.textDim }}>
                  {r.tue_ret != null ? `${(Number(r.tue_ret) * 100).toFixed(2)}%` : '—'}
                </td>
                <td style={{ padding: '6px 10px', color: T.textDim }}>
                  {r.bear_off === true ? 'BEAR_OFF' : r.bear_off === false ? '—' : ''}
                  {r.wed_ok === true ? ' WED_OK' : r.wed_ok === false ? ' WED_NO' : ''}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right',
                             color: r.action === 'TRADE' ? T.bull : T.textMuted }}>
                  {r.action === 'TRADE' && r.contracts ? `${r.contracts}c` : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right',
                             color: r.extension_count > 0 ? '#f59e0b' : T.textMuted }}>
                  {r.extension_count || 0}
                </td>
                <td style={{ padding: '6px 10px', color: T.textDim, fontSize: 10 }}>
                  {r.exit_event || '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right',
                             color: r.pl_pts > 0 ? T.bull : r.pl_pts < 0 ? T.bear : T.textMuted }}>
                  {r.pl_pts != null ? Number(r.pl_pts).toFixed(2) : '—'}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                             color: r.pl_usd > 0 ? T.bull : r.pl_usd < 0 ? T.bear : T.textMuted }}>
                  {r.pl_usd != null
                    ? `${r.pl_usd >= 0 ? '+' : ''}${Number(r.pl_usd).toFixed(0)}`
                    : '—'}
                </td>
                <td style={{ padding: '6px 10px', color: T.textMuted, fontSize: 10,
                             maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap' }}
                    title={r.block_reason || r.reason || ''}>
                  {r.block_reason || r.reason || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}
